import { useFaqs } from "@/features/faq/hooks/use-faq";
import { useMessages } from "@/features/messages/hooks/use-messages";
import { useNumberRequests } from "@/features/number-requests/hooks/use-number-requests";
import { useQualificationStats } from "@/features/qualifications/hooks/use-qualifications";
import { NUMBER_REQUEST_STATES_NEEDING_CLIENT } from "@/features/number-requests/types";

/**
 * Counts of "things waiting on a human", shown as badges in the sidebar.
 *
 * Each entry maps to exactly one menu item and is only fetched when that menu
 * item is actually visible (the caller passes the same permission/tool flags it
 * uses to build the menu), so a client without the FAQ module never issues the
 * FAQ request.
 */
export type SidebarBadges = {
  /** Submissions still to be reviewed (`status = pending`). */
  submissions: number;
  /** FAQ entries whose AI answer is newer than the human refinement. */
  faq: number;
  /** Requests customers left for the team that nobody has closed yet. */
  messages: number;
  /** Number requests stuck on the client: not started or missing documents. */
  channels: number;
};

export function useSidebarBadges({
  clientId,
  submissions = false,
  faq = false,
  messages = false,
  channels = false,
}: {
  clientId: string;
  submissions?: boolean;
  faq?: boolean;
  messages?: boolean;
  channels?: boolean;
}): SidebarBadges {
  const { data: stats } = useQualificationStats({ enabled: submissions });
  const { data: faqs } = useFaqs({ needsRefinement: true }, { enabled: faq });
  const { data: pendingMessages } = useMessages(
    { status: "pending" },
    { enabled: messages },
  );
  const { data: numberRequests } = useNumberRequests(clientId, {
    enabled: channels,
  });

  return {
    submissions: submissions ? stats?.by_status?.pending || 0 : 0,
    faq: faq ? faqs?.faqs?.length || 0 : 0,
    messages: messages ? pendingMessages?.messages?.length || 0 : 0,
    channels: channels
      ? (numberRequests || []).filter((request) =>
          NUMBER_REQUEST_STATES_NEEDING_CLIENT.includes(request.state),
        ).length
      : 0,
  };
}
